import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import SeoHead from "../components/seo/SeoHead";
import API from "../services/api";

const THEME_KEY = "hookora_theme";
const LANGUAGE_KEY = "hookora_language";

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return `₦${amount.toLocaleString()}`;
};

function AccountSettingsPage() {
  const [profile, setProfile] = useState(null);
  const [rewards, setRewards] = useState(null);
  const [loading, setLoading] = useState(true);

  const [profileForm, setProfileForm] = useState({ username: "" });
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_new_password: ""
  });

  const [theme, setTheme] = useState(
    typeof window !== "undefined" ? localStorage.getItem(THEME_KEY) || "light" : "light"
  );
  const [language, setLanguage] = useState(
    typeof window !== "undefined" ? localStorage.getItem(LANGUAGE_KEY) || "en" : "en"
  );

  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      heroBg: isDark
        ? "linear-gradient(135deg, rgba(15,23,42,0.95), rgba(30,41,59,0.88))"
        : "linear-gradient(135deg, #ffffff, #f8fafc)",
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      summaryBg: isDark ? "rgba(30,41,59,0.72)" : "#ffffff",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      metaText: isDark ? "#cbd5e1" : "#4b5563",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      hintBg: isDark ? "rgba(30,41,59,0.72)" : "#f8fafc",
      hintBorder: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      hintGood: isDark ? "#86efac" : "#15803d",
      hintMuted: isDark ? "#94a3b8" : "#6b7280",
      eyeButtonBg: isDark ? "#1e293b" : "#f3f4f6",
      eyeButtonText: isDark ? "#e2e8f0" : "#374151",
      shadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.45)"
        : "0 10px 28px rgba(15, 23, 42, 0.05)",
      softShadow: isDark
        ? "0 10px 26px rgba(2, 6, 23, 0.34)"
        : "0 8px 24px rgba(15, 23, 42, 0.04)",
      successBg: isDark ? "rgba(20,83,45,0.30)" : "#f0fdf4",
      successBorder: isDark ? "rgba(74,222,128,0.30)" : "#bbf7d0",
      successText: isDark ? "#bbf7d0" : "#166534",
      errorBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      errorBorder: isDark ? "rgba(248,113,113,0.35)" : "#fecaca",
      errorText: isDark ? "#fecaca" : "#b91c1c",
      primaryButtonBg: isDark ? "#e2e8f0" : "#111827",
      primaryButtonText: isDark ? "#020617" : "#ffffff",
      optionBg: isDark ? "#0f172a" : "#ffffff",
      optionText: isDark ? "#f8fafc" : "#111827",
      selectedOptionBorder: isDark ? "rgba(96,165,250,0.45)" : "#111827",
      selectedOptionBg: isDark ? "linear-gradient(135deg, #1d4ed8, #7c3aed)" : "#111827",
      selectedOptionText: "#ffffff",
      linkText: isDark ? "#93c5fd" : "#2563eb",
      linkButtonBg: isDark ? "#e2e8f0" : "#111827",
      linkButtonText: isDark ? "#020617" : "#ffffff",
      rewardsAccentBg: isDark ? "rgba(37,99,235,0.14)" : "#eff6ff",
      rewardsAccentBorder: isDark ? "rgba(96,165,250,0.24)" : "#bfdbfe",
      rewardsAccentText: isDark ? "#bfdbfe" : "#1d4ed8",
      walletBg: isDark ? "rgba(88,28,135,0.14)" : "#faf5ff",
      walletBorder: isDark ? "rgba(192,132,252,0.2)" : "#e9d5ff",
      walletText: isDark ? "#e9d5ff" : "#7c3aed",
    }),
    [isDark]
  );

  const themeLabel = useMemo(() => {
    return theme === "dark" ? "Dark" : "Light";
  }, [theme]);

  const languageLabel = useMemo(() => {
    const map = {
      en: "English",
      es: "Spanish",
      fr: "French",
      pt: "Portuguese"
    };
    return map[language] || "English";
  }, [language]);

  const passwordChecks = useMemo(() => {
    const password = passwordForm.new_password || "";
    return {
      minLength: password.length >= 6,
      hasLetter: /[A-Za-z]/.test(password),
      hasNumberOrSymbol: /[\d\W_]/.test(password),
      matchesConfirm:
        passwordForm.confirm_new_password.length > 0 &&
        passwordForm.new_password === passwordForm.confirm_new_password,
    };
  }, [passwordForm.new_password, passwordForm.confirm_new_password]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setLoading(true);
        const [profileResponse, rewardsResponse] = await Promise.all([
          API.get("/users/profile"),
          API.get("/referrals/me").catch(() => ({ data: null })),
        ]);

        setProfile(profileResponse.data);
        setRewards(rewardsResponse?.data || null);
        setProfileForm({ username: profileResponse.data.username || "" });
        setError("");
      } catch (err) {
        setError(err?.response?.data?.detail || "Failed to load account settings.");
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    localStorage.setItem(THEME_KEY, theme);
    document.documentElement.setAttribute("data-theme", theme);
    document.body.style.background = theme === "dark" ? "#020617" : "#f8fafc";
    document.body.style.color = theme === "dark" ? "#f8fafc" : "#111827";
    window.dispatchEvent(new CustomEvent("hookora-preferences-updated"));
  }, [theme]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    localStorage.setItem(LANGUAGE_KEY, language);
    window.dispatchEvent(new CustomEvent("hookora-preferences-updated"));
  }, [language]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();

    try {
      setProfileLoading(true);
      setMessage("");
      setError("");

      const response = await API.patch("/users/profile", {
        username: profileForm.username.trim()
      });

      setMessage(response.data.message || "Profile updated successfully.");
      setProfile((prev) => ({
        ...(prev || {}),
        username: response.data.user.username
      }));

      const existingUser = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem(
        "user",
        JSON.stringify({
          ...existingUser,
          ...response.data.user
        })
      );
      window.dispatchEvent(new CustomEvent("hookora-auth-updated"));
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update profile.");
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordForm.new_password !== passwordForm.confirm_new_password) {
      setError("New password and confirm password do not match.");
      setMessage("");
      return;
    }

    try {
      setPasswordLoading(true);
      setMessage("");
      setError("");

      const response = await API.post("/users/change-password", {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });

      setMessage(response.data.message || "Password changed successfully.");
      setPasswordForm({
        current_password: "",
        new_password: "",
        confirm_new_password: ""
      });
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmNewPassword(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to change password.");
    } finally {
      setPasswordLoading(false);
    }
  };

  const cardStyle = {
    background: colors.cardBg,
    border: `1px solid ${colors.border}`,
    borderRadius: "20px",
    padding: "22px",
    boxShadow: colors.softShadow,
    backdropFilter: isDark ? "blur(14px)" : "none"
  };

  const sectionTitleStyle = {
    marginTop: 0,
    marginBottom: "12px",
    color: colors.text,
    fontSize: "22px"
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    marginTop: "12px",
    color: colors.textSoft,
    fontWeight: "600",
    fontSize: "14px"
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    boxSizing: "border-box",
    fontSize: "15px",
    background: colors.inputBg,
    color: colors.inputText
  };

  const primaryButtonStyle = {
    marginTop: "16px",
    width: "100%",
    padding: "12px 16px",
    borderRadius: "12px",
    border: "none",
    background: colors.primaryButtonBg,
    color: colors.primaryButtonText,
    fontWeight: "700",
    cursor: "pointer"
  };

  const optionGroupStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
    gap: "10px"
  };

  const optionStyle = {
    padding: "12px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    background: colors.optionBg,
    color: colors.optionText,
    fontWeight: "600",
    cursor: "pointer"
  };

  const selectedOptionStyle = {
    padding: "12px 14px",
    borderRadius: "12px",
    border: `1px solid ${colors.selectedOptionBorder}`,
    background: colors.selectedOptionBg,
    color: colors.selectedOptionText,
    fontWeight: "700",
    cursor: "pointer",
    boxShadow: isDark ? "0 10px 22px rgba(59,130,246,0.28)" : "none"
  };

  const helperTextStyle = {
    color: colors.textMuted,
    lineHeight: "1.7",
    marginTop: 0
  };

  const metaTextStyle = {
    color: colors.metaText,
    lineHeight: "1.8"
  };

  const linkButtonStyle = {
    display: "inline-block",
    textDecoration: "none",
    background: colors.linkButtonBg,
    color: colors.linkButtonText,
    padding: "10px 14px",
    borderRadius: "10px",
    fontWeight: "700"
  };

  const plainLinkStyle = {
    color: colors.linkText,
    textDecoration: "none",
    fontWeight: "600"
  };

  const renderCheck = (passed, label) => (
    <div
      style={{
        color: passed ? colors.hintGood : colors.hintMuted,
        fontSize: "13px",
        lineHeight: "1.5",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      <span style={{ fontWeight: "700" }}>{passed ? "✓" : "•"}</span>
      <span>{label}</span>
    </div>
  );

  const passwordField = (
    value,
    onChange,
    placeholder,
    visible,
    setVisible
  ) => (
    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
      <input
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        style={{ ...inputStyle, flex: 1 }}
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={() => setVisible((prev) => !prev)}
        style={{
          minWidth: "48px",
          height: "46px",
          borderRadius: "12px",
          border: `1px solid ${colors.borderStrong}`,
          background: colors.eyeButtonBg,
          color: colors.eyeButtonText,
          cursor: "pointer",
          fontSize: "18px",
          fontWeight: "700",
        }}
        aria-label={visible ? "Hide password" : "Show password"}
        title={visible ? "Hide password" : "Show password"}
      >
        {visible ? "🙈" : "👁"}
      </button>
    </div>
  );

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: colors.pageBg, backgroundImage: colors.pageGradient }}>
        <Navbar />
        <div style={{ maxWidth: "960px", margin: "0 auto", padding: "32px 16px", color: colors.text }}>
          Loading account settings...
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: colors.pageBg, backgroundImage: colors.pageGradient }}>
      <SeoHead
        title="Account Settings | Hookora"
        description="Manage your Hookora account, password, theme, language, Reward Credits, Wallet Balance, and billing shortcuts."
        path="/settings"
      />

      <Navbar />

      <div
        style={{
          maxWidth: "980px",
          margin: "0 auto",
          padding: "28px 16px 56px"
        }}
      >
        <div
          style={{
            background: colors.heroBg,
            border: `1px solid ${colors.border}`,
            borderRadius: "22px",
            padding: "22px",
            boxShadow: colors.shadow,
            marginBottom: "18px",
            backdropFilter: isDark ? "blur(16px)" : "none"
          }}
        >
          <div style={{ marginBottom: "18px" }}>
            <h1 style={{ margin: 0, color: colors.text, fontSize: "32px", lineHeight: "1.1" }}>
              Account Settings
            </h1>
            <p style={{ marginTop: "8px", color: colors.textMuted, lineHeight: "1.7" }}>
              Manage your account, security, appearance, language, Reward Credits, Wallet Balance, and billing shortcuts.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
              gap: "12px"
            }}
          >
            <SummaryCard label="Email" value={profile?.email || "—"} colors={colors} />
            <SummaryCard label="Plan" value={profile?.plan || "free"} colors={colors} />
            <SummaryCard label="Billing" value={profile?.billing_status || "inactive"} colors={colors} />
            <SummaryCard label="Reward Credits" value={formatCurrency(rewards?.reward_credits_balance_naira || 0)} colors={colors} />
            <SummaryCard label="Wallet Balance" value={formatCurrency(profile?.wallet_balance_naira || 0)} colors={colors} />
            <SummaryCard label="Theme" value={themeLabel} colors={colors} />
            <SummaryCard label="Language" value={languageLabel} colors={colors} />
          </div>
        </div>

        {message && (
          <div
            style={{
              marginBottom: "16px",
              background: colors.successBg,
              border: `1px solid ${colors.successBorder}`,
              color: colors.successText,
              padding: "12px 14px",
              borderRadius: "14px",
              lineHeight: "1.6"
            }}
          >
            {message}
          </div>
        )}

        {error && (
          <div
            style={{
              marginBottom: "16px",
              background: colors.errorBg,
              border: `1px solid ${colors.errorBorder}`,
              color: colors.errorText,
              padding: "12px 14px",
              borderRadius: "14px",
              lineHeight: "1.6"
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "18px"
          }}
        >
          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>Profile</h2>

            <div style={metaTextStyle}>
              <div><strong>Email:</strong> {profile?.email}</div>
              <div><strong>Joined:</strong> {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : "—"}</div>
            </div>

            <form onSubmit={handleProfileSubmit} style={{ marginTop: "16px" }}>
              <label style={labelStyle}>Username</label>
              <input
                type="text"
                value={profileForm.username}
                onChange={(e) => setProfileForm({ username: e.target.value })}
                style={inputStyle}
                placeholder="Enter username"
              />

              <button type="submit" disabled={profileLoading} style={primaryButtonStyle}>
                {profileLoading ? "Saving..." : "Save Profile"}
              </button>
            </form>
          </section>

          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>Security</h2>

            <form onSubmit={handlePasswordSubmit}>
              <label style={labelStyle}>Current Password</label>
              {passwordField(
                passwordForm.current_password,
                (e) =>
                  setPasswordForm((prev) => ({
                    ...prev,
                    current_password: e.target.value
                  })),
                "Current password",
                showCurrentPassword,
                setShowCurrentPassword
              )}

              <label style={labelStyle}>New Password</label>
              {passwordField(
                passwordForm.new_password,
                (e) =>
                  setPasswordForm((prev) => ({
                    ...prev,
                    new_password: e.target.value
                  })),
                "New password",
                showNewPassword,
                setShowNewPassword
              )}

              <label style={labelStyle}>Confirm New Password</label>
              {passwordField(
                passwordForm.confirm_new_password,
                (e) =>
                  setPasswordForm((prev) => ({
                    ...prev,
                    confirm_new_password: e.target.value
                  })),
                "Confirm new password",
                showConfirmNewPassword,
                setShowConfirmNewPassword
              )}

              <div
                style={{
                  marginTop: "12px",
                  background: colors.hintBg,
                  border: `1px solid ${colors.hintBorder}`,
                  borderRadius: "12px",
                  padding: "12px",
                  display: "grid",
                  gap: "6px",
                }}
              >
                <div
                  style={{
                    color: colors.textSoft,
                    fontSize: "13px",
                    fontWeight: "700",
                    marginBottom: "2px",
                  }}
                >
                  New password should include:
                </div>
                {renderCheck(passwordChecks.minLength, "At least 6 characters")}
                {renderCheck(passwordChecks.hasLetter, "At least 1 letter")}
                {renderCheck(passwordChecks.hasNumberOrSymbol, "At least 1 number or symbol")}
                {renderCheck(passwordChecks.matchesConfirm, "New password matches confirm password")}
              </div>

              <button type="submit" disabled={passwordLoading} style={primaryButtonStyle}>
                {passwordLoading ? "Updating..." : "Change Password"}
              </button>
            </form>
          </section>

          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>Appearance</h2>
            <p style={helperTextStyle}>
              Current mode: <strong>{themeLabel}</strong>
            </p>

            <div style={optionGroupStyle}>
              <button
                type="button"
                onClick={() => setTheme("light")}
                style={theme === "light" ? selectedOptionStyle : optionStyle}
              >
                Light Mode
              </button>

              <button
                type="button"
                onClick={() => setTheme("dark")}
                style={theme === "dark" ? selectedOptionStyle : optionStyle}
              >
                Dark Mode
              </button>
            </div>

            <p style={{ ...helperTextStyle, marginTop: "14px" }}>
              Theme preference is saved across your app experience.
            </p>
          </section>

          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>Language</h2>
            <p style={helperTextStyle}>
              Current language: <strong>{languageLabel}</strong>
            </p>

            <div style={optionGroupStyle}>
              <button type="button" onClick={() => setLanguage("en")} style={language === "en" ? selectedOptionStyle : optionStyle}>English</button>
              <button type="button" onClick={() => setLanguage("es")} style={language === "es" ? selectedOptionStyle : optionStyle}>Spanish</button>
              <button type="button" onClick={() => setLanguage("fr")} style={language === "fr" ? selectedOptionStyle : optionStyle}>French</button>
              <button type="button" onClick={() => setLanguage("pt")} style={language === "pt" ? selectedOptionStyle : optionStyle}>Portuguese</button>
            </div>

            <p style={{ ...helperTextStyle, marginTop: "14px" }}>
              Your output language preference is saved for supported tools.
            </p>
          </section>

          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>Billing</h2>
            <p style={helperTextStyle}>
              Manage your plan, Reward Credits, Wallet Balance, funding, checkout, and subscription actions from your billing page.
            </p>

            <div
              style={{
                display: "grid",
                gap: "10px",
                marginBottom: "14px",
              }}
            >
              <div
                style={{
                  background: colors.rewardsAccentBg,
                  border: `1px solid ${colors.rewardsAccentBorder}`,
                  borderRadius: "14px",
                  padding: "14px",
                }}
              >
                <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "6px" }}>
                  Reward Credits
                </div>
                <div style={{ color: colors.rewardsAccentText, fontWeight: "800", fontSize: "20px" }}>
                  {formatCurrency(rewards?.reward_credits_balance_naira || 0)}
                </div>
              </div>

              <div
                style={{
                  background: colors.walletBg,
                  border: `1px solid ${colors.walletBorder}`,
                  borderRadius: "14px",
                  padding: "14px",
                }}
              >
                <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "6px" }}>
                  Wallet Balance
                </div>
                <div style={{ color: colors.walletText, fontWeight: "800", fontSize: "20px" }}>
                  {formatCurrency(profile?.wallet_balance_naira || 0)}
                </div>
              </div>
            </div>

            <Link to="/app/pricing" style={linkButtonStyle}>
              Go to Billing
            </Link>
          </section>

          <section style={cardStyle}>
            <h2 style={sectionTitleStyle}>Support & Policies</h2>
            <div style={{ display: "grid", gap: "10px" }}>
              <Link to="/contact-support" style={plainLinkStyle}>Contact Support</Link>
              <Link to="/refund-policy" style={plainLinkStyle}>Refund Policy</Link>
              <Link to="/privacy-policy" style={plainLinkStyle}>Privacy Policy</Link>
              <Link to="/terms" style={plainLinkStyle}>Terms of Service</Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, colors }) {
  return (
    <div
      style={{
        background: colors.summaryBg,
        border: `1px solid ${colors.border}`,
        borderRadius: "16px",
        padding: "14px 16px",
        backdropFilter: "blur(10px)"
      }}
    >
      <div style={{ color: colors.textMuted, fontSize: "13px", marginBottom: "6px" }}>
        {label}
      </div>
      <div
        style={{
          color: colors.text,
          fontSize: "16px",
          fontWeight: "700",
          lineHeight: "1.5",
          wordBreak: "break-word"
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default AccountSettingsPage;